import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Search, Phone, Mail, X, Users, AlertCircle, Building2 } from 'lucide-react';
import { Customer, getCustomersPaged } from '../../lib/customers';
import { useBusiness } from '../../contexts/BusinessContext';
import { getPersonInitials } from '../../utils/personInitials';
import CustomerModal from '../CustomerModal';
import {
  SELECT_CUSTOMER_PAGE_SIZE,
  invalidateSelectCustomerCache,
  peekSelectCustomerCache,
  prefetchSelectCustomers,
  rememberSelectCustomerPage,
} from '../../lib/customers/selectCustomerCache';
import { ActiveCustomerStatus, getActiveCustomerStatus } from '../../lib/customers/activeStatus';
import ActiveCustomerBadge from '../customers/ActiveCustomerBadge';

interface SelectCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (customer: Customer) => void;
  baseZIndex?: number; // Optional base z-index for layering above other modals
}

function CustomerRepeatBadge({ customer }: { customer: Customer }) {
  const isRepeat = customer.repeat === true;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        isRepeat
          ? 'bg-green-100 text-green-800 border border-green-200'
          : 'bg-blue-100 text-blue-800 border border-blue-200'
      }`}
    >
      {isRepeat ? 'Repeat' : 'New'} Customer
    </span>
  );
}

export default function SelectCustomerModal({
  isOpen,
  onClose,
  onSelect,
  baseZIndex = 100200,
}: SelectCustomerModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  // Active Customer badges (sales feature) — the list itself is unchanged.
  const [activeStatus, setActiveStatus] = useState<ActiveCustomerStatus | null>(null);
  useEffect(() => {
    if (!isOpen || !selectedBusiness?.id) return;
    let cancelled = false;
    getActiveCustomerStatus(selectedBusiness.id).then(s => {
      if (!cancelled) setActiveStatus(s);
    });
    return () => { cancelled = true; };
  }, [isOpen, selectedBusiness?.id]);

  // Debounce search so we don't hit the server on every keystroke.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchQuery]);

  const loadPage = async (opts: {
    page: number;
    search: string;
    append: boolean;
    showSpinner: boolean;
  }) => {
    if (!selectedBusiness?.id) return;
    const businessId = selectedBusiness.id;
    const requestId = ++requestIdRef.current;

    try {
      if (opts.showSpinner) setLoading(true);
      if (opts.append) setLoadingMore(true);
      setError(null);

      const { rows, total } =
        opts.page === 1 && !opts.search
          ? await prefetchSelectCustomers(businessId)
          : await getCustomersPaged(businessId, {
              page: opts.page,
              pageSize: SELECT_CUSTOMER_PAGE_SIZE,
              search: opts.search,
            });

      if (requestId !== requestIdRef.current) return;

      setTotalCount(total);
      setPage(opts.page);
      if (opts.append) {
        setCustomers((prev) => {
          const seen = new Set(prev.map((c) => c.id));
          return [...prev, ...rows.filter((c) => !seen.has(c.id))];
        });
      } else {
        setCustomers(rows);
        if (!opts.search) {
          rememberSelectCustomerPage(businessId, rows, total);
        }
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error loading customers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load customers');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    if (!isOpen || !selectedBusiness?.id) return;

    setIsAnimating(true);
    const businessId = selectedBusiness.id;
    const cached = !debouncedSearch ? peekSelectCustomerCache(businessId) : null;

    if (cached) {
      setCustomers(cached.rows);
      setTotalCount(cached.total);
      setPage(1);
      setLoading(false);
      // Refresh quietly in the background so the list stays current.
      void loadPage({
        page: 1,
        search: '',
        append: false,
        showSpinner: false,
      });
      return;
    }

    void loadPage({
      page: 1,
      search: debouncedSearch,
      append: false,
      showSpinner: true,
    });
    // Intentionally depend on open + search + business
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedBusiness?.id, debouncedSearch]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setDebouncedSearch('');
      setError(null);
      setPage(1);
      setLoadingMore(false);
    }
  };

  const handleCustomerCreated = async (newCustomer: Customer) => {
    setIsCustomerModalOpen(false);
    if (selectedBusiness?.id) {
      invalidateSelectCustomerCache(selectedBusiness.id);
    }
    setCustomers((prev) => [newCustomer, ...prev.filter((c) => c.id !== newCustomer.id)]);
    setTotalCount((prev) => prev + 1);
    onSelect(newCustomer);
  };

  const hasMore = customers.length < totalCount;
  const handleLoadMore = () => {
    if (loadingMore || loading || !hasMore) return;
    void loadPage({
      page: page + 1,
      search: debouncedSearch,
      append: true,
      showSpinner: false,
    });
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div
      data-select-customer-modal
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
      style={{
        zIndex: baseZIndex,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0',
      }}
    >
      <div
        data-select-customer-modal
        className={`bg-white transform transition-all duration-200 ${
          isMobile
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: baseZIndex + 10,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <h2
              className="text-2xl font-bold text-gray-900"
              style={{ letterSpacing: '-0.02em' }}
            >
              Select Customer
            </h2>
          </div>

          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 w-full"
                  style={{ fontSize: '16px' }}
                />
              </div>
              <button
                onClick={() => setIsCustomerModalOpen(true)}
                className="inline-flex items-center justify-center px-4 py-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm whitespace-nowrap"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Plus className="h-4 w-4 mr-1.5" />
                New Customer
              </button>
            </div>
          </div>

          <div
            className="flex-1 overflow-y-auto bg-white"
            style={{
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0',
            }}
          >
            <div
              className={`${isMobile ? 'p-4' : 'p-6'} space-y-4`}
              style={{
                paddingBottom: isMobile
                  ? 'calc(1rem + env(safe-area-inset-bottom, 0px))'
                  : '1rem',
              }}
            >
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {loading && customers.length === 0 ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                    <p className="mt-4 text-sm text-gray-600">Loading customers...</p>
                  </div>
                </div>
              ) : customers.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No customers found
                  </h3>
                  <p className="text-sm text-gray-600">
                    {searchQuery
                      ? 'Try adjusting your search terms'
                      : 'Get started by adding your first customer'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {customers.map((customer) => (
                      <div
                        key={customer.id}
                        data-assistant-customer-row={`${customer.first_name || ''} ${customer.last_name || ''}`.trim()}
                        data-assistant-customer-detail={customer.email || customer.phone || ''}
                        role="button"
                        aria-label={`${`${customer.first_name || ''} ${customer.last_name || ''}`.trim()}${customer.email || customer.phone ? ` (${customer.email || customer.phone})` : ''}`}
                        className="bg-white rounded-[12px] border border-gray-200/60 p-2 relative transition-all duration-150 shadow-[0_1px_2px_rgba(0,0,0,0.05)] cursor-pointer active:scale-[0.98] touch-manipulation"
                        onClick={() => onSelect(customer)}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <div className="flex items-center space-x-2.5 mb-2">
                          <div className="h-6 w-6 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-[10px] flex-shrink-0">
                            {getPersonInitials(customer.first_name, customer.last_name)}
                          </div>
                          <div className="flex-1 min-w-0 leading-tight">
                            <div className="text-[14px] font-semibold text-gray-900 truncate">
                              {customer.first_name} {customer.last_name}
                            </div>
                            {customer.company && (
                              <div className="text-[12px] text-gray-500 truncate flex items-center gap-1">
                                <Building2 className="h-3 w-3 text-gray-400 flex-shrink-0" />
                                {customer.company}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {activeStatus?.enabled && (
                              <ActiveCustomerBadge active={activeStatus.activeIds.has(customer.id)} />
                            )}
                            <CustomerRepeatBadge customer={customer} />
                          </div>
                        </div>

                        <div className="border-t border-gray-200/60 my-2"></div>

                        <div className="space-y-1.5 pt-1">
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <Mail className="h-3.5 w-3.5 text-gray-400" />
                            <span className="truncate">{customer.email}</span>
                          </div>
                          {customer.phone && (
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                              <Phone className="h-3.5 w-3.5 text-gray-400" />
                              <span>{customer.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {hasMore && (
                    <button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="w-full px-4 py-3 text-sm font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 border border-blue-200 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation disabled:opacity-60"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      {loadingMore
                        ? 'Loading…'
                        : `Load More Customers (${customers.length} of ${totalCount})`}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}

      <CustomerModal
        isOpen={isCustomerModalOpen}
        onClose={() => setIsCustomerModalOpen(false)}
        onSuccess={handleCustomerCreated}
        businessId={selectedBusiness?.id || ''}
        baseZIndex={baseZIndex + 100}
      />
    </>
  );
}
